'use client';

import { useState, useEffect } from 'react';
import { format, isValid, parse, parseISO } from 'date-fns';
import { DateField } from '@/components/ui/DateField';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { useToast } from '@/lib/context/ToastContext';
import { useAuth } from '@/lib/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/Input';
import { PhoneInput } from '@/components/ui/PhoneInput';
import { isValidE164 } from '@/lib/utils/phone';
import { useSpecialties } from '@/lib/swr';
import { CountryField, RegionField, EducationField, DateOfBirthField } from '@/components/forms/ProfileFields';
import SpecialtiesField, { SpecialtiesValue } from '@/components/forms/SpecialtiesField';
import { HealthcareRoleField } from '@/components/forms/HealthcareRoleField';
import { NotWorkingCheckbox } from '@/components/forms/NotWorkingCheckbox';
import { specialtyHiddenFor, specialtyRequiredFor, type HealthcareRole } from '@/lib/specialties/types';
import { profileErrorMessageKey } from '@/lib/profile/errorMessage';

interface FieldOption {
  value: string;
  label_ua: string;
  label_en?: string;
}

interface AdditionalField {
  id: string;
  field_name: string;
  field_type: 'text' | 'textarea' | 'select' | 'radio' | 'checkbox' | 'date' | 'phone' | 'email' | 'number';
  label_ua: string;
  label_en?: string;
  placeholder_ua?: string;
  placeholder_en?: string;
  is_required: boolean;
  validation_rules?: Record<string, unknown>;
  field_options?: {
    options: FieldOption[];
  };
  sort_order?: number;
}

interface AdditionalProfileFieldsFormProps {
  courseId: string;
  onSubmit: (data: Record<string, string>) => void;
  onCancel?: () => void;
  submitText?: string;
  cancelText?: string;
  loading?: boolean;
}

export default function AdditionalProfileFieldsForm({
  courseId,
  onSubmit,
  onCancel,
  submitText = 'Продовжити',
  cancelText = 'Скасувати',
  loading: externalLoading = false
}: AdditionalProfileFieldsFormProps) {
  const { t, language } = useLanguage();
  const toast = useToast();
  const { user, refreshProfile } = useAuth();
  const { data: specialtiesData } = useSpecialties();
  const specialtyOptions = specialtiesData ?? [];
  const [fields, setFields] = useState<AdditionalField[]>([]);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const parseStoredDate = (value?: string): Date | null => {
    if (!value) return null;
    const trimmed = value.trim();

    try {
      const isoParsed = parseISO(trimmed);
      if (isValid(isoParsed)) {
        return isoParsed;
      }
    } catch {
      // Ignore parse errors for non-ISO strings
    }

    const ymdParsed = parse(trimmed, 'yyyy-MM-dd', new Date());
    if (isValid(ymdParsed)) {
      return ymdParsed;
    }

    const europeanParsed = parse(trimmed, 'dd.MM.yyyy', new Date());
    if (isValid(europeanParsed)) {
      return europeanParsed;
    }

    const nativeDate = new Date(trimmed);
    return isValid(nativeDate) ? nativeDate : null;
  };

  const handleDateChange = (fieldName: string, date: Date | null) => {
    const formatted = date ? format(date, 'yyyy-MM-dd') : '';
    handleInputChange(fieldName, formatted);
  };

  useEffect(() => {
    fetchProfileCompletion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const fetchProfileCompletion = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/courses/${courseId}/profile-completion`);
      const data = await response.json();

      if (response.ok) {
        // Override is_required for specific profile fields
        const REQUIRED_OVERRIDES = new Set([
          'date_of_birth', 'country', 'region', 'education', 'workplace'
        ]);
        const fieldsWithOverrides = (data.requiredFields || []).map((f: AdditionalField) => ({
          ...f,
          is_required: REQUIRED_OVERRIDES.has(f.field_name) ? true : f.is_required,
        }));
        setFields(fieldsWithOverrides);

        // Combine basic profile data with additional data.
        // Basic profile fields from user_profiles take precedence –
        // additional data must not overwrite them.
        const basicKeys = new Set(Object.keys(data.userBasicData || {}));
        const filteredAdditional = Object.fromEntries(
          Object.entries(data.userAdditionalData || {}).filter(([key]) => !basicKeys.has(key))
        );
        const combinedData = {
          ...data.userBasicData,
          ...filteredAdditional
        };
        setFormData(combinedData || {});

      } else {
        console.error('Failed to fetch profile completion:', data.error);
      }
    } catch (error) {
      console.error('Error fetching profile completion:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateField = (field: AdditionalField, value: string): string | null => {
    if (field.field_name === 'specialty_id') {
      // Requiredness follows the role selected RIGHT NOW, never field.is_required:
      // that flag reflects the role stored when the field list was fetched, so a
      // legacy profile (healthcare_role null) arrives with is_required false and
      // would let a freshly picked 'practitioner' through with no specialty.
      // The reverse case matters too - switching away from 'practitioner' must
      // stop demanding one.
      //
      // An empty catalog (still loading, or the fetch failed) can offer no
      // specialty to pick, so demanding one there would make the form
      // unfinishable - the server's own completion check has the same
      // `specialties.length > 0` guard.
      const currentRole = formData.healthcare_role as HealthcareRole | '';
      if (currentRole === 'practitioner' && specialtyOptions.length > 0 && (!value || value.trim() === '')) {
        return t('additionalFields.validation.required');
      }
      return null;
    }

    if (field.is_required && (!value || value.trim() === '')) {
      return t('additionalFields.validation.required');
    }

    if (field.field_type === 'email' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return t('additionalFields.validation.invalidEmail');
      }
    }

    if (field.field_type === 'phone' && value) {
      if (!isValidE164(value)) {
        return t('phone.invalid');
      }
    }

    if (field.validation_rules) {
      const rules = field.validation_rules;

      if (rules.min_length && value.length < (rules.min_length as number)) {
        return t('additionalFields.validation.minLength').replace('{count}', String(rules.min_length));
      }

      if (rules.max_length && value.length > (rules.max_length as number)) {
        return t('additionalFields.validation.maxLength').replace('{count}', String(rules.max_length));
      }

      if (rules.pattern && value) {
        const regex = new RegExp(rules.pattern as string);
        if (!regex.test(value)) {
          return t('additionalFields.validation.invalidFormat');
        }
      }
    }

    return null;
  };

  const handleInputChange = (fieldName: string, value: string) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [fieldName]: value
      };

      // If country changed, clear region field
      if (fieldName === 'country') {
        newData['region'] = '';
      }

      return newData;
    });

    // Clear error when user starts typing
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }

    // Also clear region error if country changed
    if (fieldName === 'country' && errors['region']) {
      setErrors(prev => ({
        ...prev,
        region: ''
      }));
    }
  };

  const getSpecialtiesValue = (): SpecialtiesValue => {
    let secondary: string[] = [];
    try {
      const parsed = JSON.parse(formData.secondary_specialty_ids || '[]');
      if (Array.isArray(parsed)) secondary = parsed;
    } catch {
      secondary = [];
    }
    return {
      primary: formData.specialty_id || null,
      secondary,
      other: formData.specialty_other || '',
    };
  };

  const setSpecialtiesValue = (value: SpecialtiesValue) => {
    setFormData(prev => ({
      ...prev,
      specialty_id: value.primary || '',
      secondary_specialty_ids: JSON.stringify(value.secondary),
      specialty_other: value.other,
    }));

    if (errors['specialty_id']) {
      setErrors(prev => ({
        ...prev,
        specialty_id: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all fields
    const newErrors: Record<string, string> = {};
    fields.forEach(field => {
      // Hidden when not currently working - a stale required flag from the
      // fetch that predates this toggle must not block submission.
      if ((field.field_name === 'workplace' || field.field_name === 'position') && formData.is_not_working === 'true') {
        return;
      }
      const value = formData[field.field_name] || '';
      const error = validateField(field, value);
      if (error) {
        newErrors[field.field_name] = error;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);

      // Show toast about incomplete fields
      toast.error(t('additionalFields.validation.fillAllRequired'));

      // Scroll to the first field with an error
      const firstErrorField = fields.find(f => newErrors[f.field_name]);
      if (firstErrorField) {
        const el = document.querySelector(`[data-field-name="${firstErrorField.field_name}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }

      return;
    }

    setSubmitting(true);
    try {
      // Separate basic profile fields from any other form data
      const basicFields = ['first_name', 'last_name', 'patronymic', 'phone', 'date_of_birth',
                          'country', 'region', 'city', 'address', 'postal_code',
                          'education', 'education_other', 'workplace', 'position',
                          'specialty_id', 'secondary_specialty_ids', 'specialty_other',
                          'healthcare_role', 'is_not_working'];
      const basicProfileData: Record<string, unknown> = {};

      Object.entries(formData).forEach(([key, value]) => {
        if (!basicFields.includes(key)) return;

        if (key === 'is_not_working') {
          basicProfileData[key] = value === 'true';
        } else if (key === 'secondary_specialty_ids') {
          try {
            basicProfileData[key] = JSON.parse(value || '[]');
          } catch {
            basicProfileData[key] = [];
          }
        } else {
          basicProfileData[key] = value;
        }
      });

      // Update basic profile fields if any were changed
      if (Object.keys(basicProfileData).length > 0) {
        const profileResponse = await fetch('/api/profile', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(basicProfileData),
        });

        if (!profileResponse.ok) {
          const profileData = await profileResponse.json().catch(() => ({}));
          console.error('Failed to update profile:', profileData);
          // 'invalid_phone' / 'unknown_specialty' are machine codes the route
          // answers with - show the user which field to fix, not the code.
          const key = profileErrorMessageKey(profileData?.error);
          toast.error(key ? t(key) : t('errors.errorSavingData'));
          return;
        }

        // Invalidate profile cache to ensure fresh data
        if (user?.id) {
          await refreshProfile(user.id, true);
        }
      }

      onSubmit(formData);
    } catch (error) {
      console.error('Error saving data:', error);
      toast.error(t('errors.errorSavingData'));
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field: AdditionalField) => {
    const value = formData[field.field_name] || '';
    const error = errors[field.field_name];
    const label = language === 'uk'
      ? (field.label_ua || field.field_name)
      : (field.label_en || field.label_ua || field.field_name);
    const placeholder = language === 'uk'
      ? (field.placeholder_ua || '')
      : (field.placeholder_en || field.placeholder_ua || '');

    const commonClasses = `w-full px-3 py-2 border rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-blue-500 ${
      error ? 'border-red-500' : 'border-gray-300'
    }`;

    // Shared profile field components (searchable, sorted)
    if (field.field_name === 'country') {
      return (
        <div key={field.field_name}>
          <CountryField
            value={value}
            onChange={(v) => handleInputChange('country', v)}
            language={language}
            t={t}
            error={error}
            required={field.is_required}
          />
        </div>
      );
    }

    if (field.field_name === 'region') {
      const countryValue = formData['country'] || '';
      return (
        <div key={field.field_name}>
          <RegionField
            value={value}
            onChange={(v) => handleInputChange('region', v)}
            countryValue={countryValue}
            language={language}
            t={t}
            error={error}
            required={field.is_required}
          />
        </div>
      );
    }

    if (field.field_name === 'healthcare_role') {
      return (
        <div key={field.field_name}>
          <HealthcareRoleField
            value={(formData.healthcare_role || null) as HealthcareRole | null}
            onChange={(role) => handleInputChange('healthcare_role', role)}
            required={field.is_required}
            error={error}
          />
        </div>
      );
    }

    if (field.field_name === 'specialty_id') {
      const currentRole = (formData.healthcare_role || null) as HealthcareRole | null;
      if (specialtyHiddenFor(currentRole)) {
        return null;
      }
      return (
        <div key={field.field_name}>
          <SpecialtiesField
            value={getSpecialtiesValue()}
            onChange={setSpecialtiesValue}
            options={specialtyOptions}
            role={currentRole}
            // Same reason as validateField above: field.is_required is a
            // snapshot of the STORED role, so the asterisk has to follow the
            // role picked right now, not the one the fetch saw.
            required={specialtyRequiredFor(currentRole)}
            error={error}
            compact
          />
        </div>
      );
    }

    if (field.field_name === 'is_not_working') {
      return (
        <div key={field.field_name}>
          <NotWorkingCheckbox
            checked={formData.is_not_working === 'true'}
            onChange={(checked) => handleInputChange('is_not_working', checked ? 'true' : '')}
          />
        </div>
      );
    }

    if ((field.field_name === 'workplace' || field.field_name === 'position') && formData.is_not_working === 'true') {
      return null;
    }

    if (field.field_name === 'education') {
      return (
        <div key={field.field_name}>
          <EducationField
            value={value}
            onChange={(v) => handleInputChange('education', v)}
            otherValue={formData.education_other || ''}
            onOtherChange={(v) => handleInputChange('education_other', v)}
            language={language}
            t={t}
            error={error}
            required={field.is_required}
          />
        </div>
      );
    }

    if (field.field_name === 'date_of_birth') {
      return (
        <div key={field.field_name}>
          <DateOfBirthField
            value={parseStoredDate(value)}
            onChange={(date) => handleDateChange(field.field_name, date)}
            label={label}
            error={error}
            required={field.is_required}
            placeholder={language === 'uk' ? 'дд.мм.рррр' : 'dd.mm.yyyy'}
          />
        </div>
      );
    }

    switch (field.field_type) {
      case 'textarea':
        return (
          <div key={field.field_name} className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              {label}
              {field.is_required && <span className="text-red-500">*</span>}
            </label>
            <textarea
              value={value}
              onChange={(e) => handleInputChange(field.field_name, e.target.value)}
              placeholder={placeholder}
              rows={3}
              className={commonClasses}
            />
            {error && <p className="text-red-500 text-sm">{error}</p>}
          </div>
        );

      case 'select':
        return (
          <div key={field.field_name} className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              {label}
              {field.is_required && <span className="text-red-500">*</span>}
            </label>
            <select
              value={value}
              onChange={(e) => handleInputChange(field.field_name, e.target.value)}
              className={commonClasses}
            >
              <option value="">{t('additionalFields.selectOption')}</option>
              {field.field_options?.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {(language !== 'uk' && option.label_en) ? option.label_en : option.label_ua}
                </option>
              ))}
            </select>
            {error && <p className="text-red-500 text-sm">{error}</p>}
          </div>
        );

      case 'radio':
        return (
          <div key={field.field_name} className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              {label}
              {field.is_required && <span className="text-red-500">*</span>}
            </label>
            <div className="space-y-2">
              {field.field_options?.options.map((option) => (
                <label key={option.value} className="flex items-center">
                  <input
                    type="radio"
                    name={field.field_name}
                    value={option.value}
                    checked={value === option.value}
                    onChange={(e) => handleInputChange(field.field_name, e.target.value)}
                    className="mr-2"
                  />
                  {(language !== 'uk' && option.label_en) ? option.label_en : option.label_ua}
                </label>
              ))}
            </div>
            {error && <p className="text-red-500 text-sm">{error}</p>}
          </div>
        );

      case 'checkbox':
        return (
          <div key={field.field_name} className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={value === 'true'}
                onChange={(e) => handleInputChange(field.field_name, e.target.checked ? 'true' : 'false')}
                className="mr-2"
              />
              {label}
              {field.is_required && <span className="text-red-500">*</span>}
            </label>
            {error && <p className="text-red-500 text-sm">{error}</p>}
          </div>
        );

      case 'date':
        return (
          <div key={field.field_name} className="space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              {label}
              {field.is_required && <span className="text-red-500">*</span>}
            </label>
            <DateField
              value={parseStoredDate(value)}
              onChange={(date) => handleDateChange(field.field_name, date)}
              placeholder={placeholder || 'дд.мм.рррр'}
              className="w-full"
              allowClear={!field.is_required}
              maxDate={field.field_name === 'date_of_birth' ? new Date() : undefined}
            />
            {error && <p className="text-red-500 text-sm">{error}</p>}
          </div>
        );

      case 'number':
        return (
          <Input
            key={field.field_name}
            type="number"
            value={value}
            onChange={(e) => handleInputChange(field.field_name, e.target.value)}
            placeholder={placeholder}
            label={label}
            error={error}
            required={field.is_required}
          />
        );

      case 'phone':
        return (
          <PhoneInput
            key={field.field_name}
            value={value}
            onChange={(v) => handleInputChange(field.field_name, v)}
            placeholder={placeholder}
            label={label}
            required={field.is_required}
            error={error}
          />
        );

      default:
        return (
          <Input
            key={field.field_name}
            type={field.field_type === 'email' ? 'email' : 'text'}
            value={value}
            onChange={(e) => handleInputChange(field.field_name, e.target.value)}
            placeholder={placeholder}
            label={label}
            error={error}
            required={field.is_required}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (fields.length === 0) {
    return null; // No additional fields required
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {t('additionalFields.title')}
        </h3>
        <p className="text-gray-600 text-sm">
          {t('additionalFields.description')}
        </p>
      </div>

      {fields.map(field => (
        <div key={field.field_name} data-field-name={field.field_name}>
          {renderField(field)}
        </div>
      ))}

      <div className="flex gap-3 pt-4">
        {onCancel && (
          <Button
            type="button"
            variant="secondary"
            className="flex-1"
            onClick={onCancel}
            disabled={submitting || externalLoading}
          >
            {cancelText}
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          className="flex-1"
          disabled={submitting || externalLoading}
        >
          {submitting || externalLoading ? t('additionalFields.saving') : submitText}
        </Button>
      </div>
    </form>
  );
}
